/**
 * Charts API Client
 * ==================
 * API functions for periodontal chart operations.
 */

import { apiClient } from './client';
import type {
  Chart,
  ChartBrief,
  ToothData,
  PaginatedResponse,
  ChartSummaryStats,
} from '@/types';

// =============================================================================
// TYPES
// =============================================================================

export interface ChartCreateRequest {
  patient_id: string;
  exam_date?: string;
  exam_type?: 'comprehensive' | 'periodic' | 'limited';
  provider_id?: string;
  hygienist_id?: string;
  clinical_notes?: string;
  tooth_data?: ToothData[];
  is_baseline?: boolean;
  compare_to_chart_id?: string;
}

export interface ChartUpdateRequest {
  exam_date?: string;
  exam_type?: 'comprehensive' | 'periodic' | 'limited';
  provider_id?: string;
  hygienist_id?: string;
  clinical_notes?: string;
  tooth_data?: ToothData[];
  aap_stage?: string;
  aap_grade?: string;
  aap_extent?: string;
  risk_level?: string;
  risk_notes?: string;
  risk_factors?: string[];
  referral_recommended?: boolean;
  referral_reason?: string;
}

export interface ChartAutosaveRequest {
  tooth_data?: ToothData[];
  clinical_notes?: string;
  input_method?: 'manual' | 'voice' | 'import';
}

export interface AutosaveResponse {
  id: string;
  last_autosave_at: string;
  autosave_count: number;
  tooth_data?: ToothData[]; // Make sure this is present
}

export interface ChartFinalizeRequest {
  clinical_notes?: string;
  aap_stage?: string;
  aap_grade?: string;
  aap_extent?: string;
  signature_confirmation: boolean;
}

export interface ChartSearchParams {
  patient_id?: string;
  status?: 'draft' | 'finalized' | 'amended';
  provider_id?: string;
  page?: number;
  page_size?: number;
  sort_order?: 'asc' | 'desc';
}

export interface ChartComparisonResponse {
  current: Chart;
  baseline: Chart;
  changes_summary: {
    improved_sites: number;
    worsened_sites: number;
    stable_sites: number;
    avg_depth_change: number;
    bop_change: number;
  };
}

export interface ChartExportRequest {
  chart_id: string;
  include_comparison?: boolean;
  comparison_chart_id?: string;
  include_images?: boolean;
  template?: 'standard' | 'detailed' | 'summary';
}

export interface ChartExportResponse {
  chart_id: string;
  export_format: string;
  download_url: string;
  expires_at: string;
  file_size_bytes: number;
}

export interface ChartLockResponse {
  locked: boolean;
  locked_until?: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * List charts with pagination and filters.
 */
export async function listCharts(
  params: ChartSearchParams = {}
): Promise<PaginatedResponse<ChartBrief>> {
  const response = await apiClient.get<PaginatedResponse<ChartBrief>>('/charts', {
    params,
  });
  return response.data;
}

/**
 * Get patient's charts.
 */
export async function getPatientCharts(
  patientId: string,
  includeDrafts = true
): Promise<ChartBrief[]> {
  const response = await apiClient.get<ChartBrief[]>(
    `/charts/patient/${patientId}`,
    {
      params: { include_drafts: includeDrafts },
    }
  );
  return response.data;
}

/**
 * Get chart by ID.
 */
export async function getChart(chartId: string): Promise<Chart> {
  const response = await apiClient.get<Chart>(`/charts/${chartId}`);
  return response.data;
}

/**
 * Create a new chart.
 */
export async function createChart(data: ChartCreateRequest): Promise<Chart> {
  const response = await apiClient.post<Chart>('/charts', data);
  return response.data;
}

/**
 * Update chart.
 */
export async function updateChart(
  chartId: string,
  data: ChartUpdateRequest
): Promise<Chart> {
  const response = await apiClient.patch<Chart>(`/charts/${chartId}`, data);
  return response.data;
}

/**
 * Autosave chart data.
 */
export async function autosaveChart(
  chartId: string,
  data: {
    tooth_data?: ToothData[];
    clinical_notes?: string;
    input_method?: string;
    // AAP Classification
    aap_stage?: string;
    aap_grade?: string;
    aap_extent?: string;
    // Risk Factors
    risk_factors?: string[];
    // Referral
    referral_recommended?: boolean;
    referral_reason?: string;
  }
): Promise<AutosaveResponse> {
  try {
    console.log('Calling autosave API with data:', data);

    const response = await apiClient.post(`/charts/${chartId}/autosave`, data);

    console.log('Raw autosave API response:', response.data);

    return response.data;
  } catch (error) {
    console.error('Autosave API error:', error);
    throw error;
  }
}

/**
 * Finalize chart.
 */
export async function finalizeChart(
  chartId: string,
  data: ChartFinalizeRequest
): Promise<Chart> {
  const response = await apiClient.post<Chart>(
    `/charts/${chartId}/finalize`,
    data
  );
  return response.data;
}

/**
 * Delete chart (draft only).
 */
export async function deleteChart(chartId: string): Promise<void> {
  await apiClient.delete(`/charts/${chartId}`);
}

/**
 * Compare two charts.
 */
export async function compareCharts(
  chartId: string,
  baselineId: string
): Promise<ChartComparisonResponse> {
  const response = await apiClient.get<ChartComparisonResponse>(
    `/charts/${chartId}/compare/${baselineId}`
  );
  return response.data;
}

/**
 * Acquire edit lock on chart.
 */
export async function acquireChartLock(chartId: string): Promise<ChartLockResponse> {
  const response = await apiClient.post<ChartLockResponse>(
    `/charts/${chartId}/lock`
  );
  return response.data;
}

/**
 * Release edit lock on chart.
 */
export async function releaseChartLock(chartId: string): Promise<void> {
  await apiClient.post(`/charts/${chartId}/unlock`);
}

/**
 * Export chart as PDF.
 */
export async function exportChart(
  data: ChartExportRequest
): Promise<ChartExportResponse> {
  const response = await apiClient.post<ChartExportResponse>(
    `/charts/${data.chart_id}/export`,
    data
  );
  return response.data;
}

/**
 * Download exported chart PDF.
 */
export async function downloadChartPDF(chartId: string): Promise<Blob> {
  const response = await apiClient.get(`/charts/${chartId}/export/pdf`, {
    responseType: 'blob',
  });
  return response.data;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate summary statistics from tooth data.
 */
export function calculateSummaryStats(toothData: ToothData[]): ChartSummaryStats {
  let teethPresent = 0;
  let teethMissing = 0;
  let teethImplants = 0;
  let sitesMeasured = 0;
  let sitesBleeding = 0;
  let sitesSuppuration = 0;
  let totalDepth = 0;
  let maxDepth = 0;
  let pockets4mm = 0;
  let pockets5mm = 0;
  let pockets6mm = 0;
  let mobilityCount = 0;
  let furcationCount = 0;

  for (const tooth of toothData) {
    if (tooth.status === 'present') {
      teethPresent++;
    } else if (tooth.status === 'missing') {
      teethMissing++;
      continue;
    } else if (tooth.status === 'implant') {
      teethImplants++;
    }

    if (tooth.mobility && tooth.mobility >= 1) {
      mobilityCount++;
    }
    if (tooth.furcation && tooth.furcation >= 1) {
      furcationCount++;
    }

    if (tooth.sites) {
      for (const site of tooth.sites) {
        if (site.pocket_depth !== null && site.pocket_depth !== undefined) {
          sitesMeasured++;
          totalDepth += site.pocket_depth;
          maxDepth = Math.max(maxDepth, site.pocket_depth);

          if (site.pocket_depth >= 4) pockets4mm++;
          if (site.pocket_depth >= 5) pockets5mm++;
          if (site.pocket_depth >= 6) pockets6mm++;
        }

        if (site.bleeding) sitesBleeding++;
        if (site.suppuration) sitesSuppuration++;
      }
    }
  }

  const avgDepth = sitesMeasured > 0 ? totalDepth / sitesMeasured : 0;
  const bopPercentage = sitesMeasured > 0 ? (sitesBleeding / sitesMeasured) * 100 : 0;

  return {
    teeth_present: teethPresent,
    teeth_missing: teethMissing,
    teeth_implants: teethImplants,
    sites_measured: sitesMeasured,
    sites_bleeding: sitesBleeding,
    sites_suppuration: sitesSuppuration,
    bop_percentage: Math.round(bopPercentage * 10) / 10,
    avg_pocket_depth: Math.round(avgDepth * 10) / 10,
    max_pocket_depth: maxDepth,
    pockets_4mm_plus: pockets4mm,
    pockets_5mm_plus: pockets5mm,
    pockets_6mm_plus: pockets6mm,
    mobility_grade_1_plus: mobilityCount,
    furcation_grade_1_plus: furcationCount,
  };
}

// =============================================================================
// VOICE INPUT
// =============================================================================

export interface VoiceInputUploadResponse {
  chart_id: string;
  voice_input_filename: string;
  transcription?: string;
  extracted_data?: any;
  message: string;
}

/**
 * Upload voice recording for a chart
 */
export async function uploadVoiceInput(
  chartId: string,
  audioBlob: Blob
): Promise<VoiceInputUploadResponse> {
  const formData = new FormData();

  // Create a filename with timestamp
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const filename = `voice_${chartId.substring(0, 8)}_${timestamp}.webm`;

  formData.append('audio_file', audioBlob, filename);

  const response = await apiClient.post(
    `/charts/${chartId}/voice-input`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
}
